import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const MAX_TRIES = 15;
const VIEWS = {
  processing: [Loader2, "text-blue-600 animate-spin", "pay.processing"],
  success: [CheckCircle2, "text-emerald-500", "pay.success"],
  failed: [XCircle, "text-red-500", "pay.failed"],
  cancel: [XCircle, "text-slate-400", "pay.cancel"],
};

// Premium is only granted once the backend has verified the payment with Stripe
function usePaymentStatus(sessionId, enabled, refresh) {
  const [state, setState] = useState(enabled ? "processing" : "cancel");
  // Latest refresh via ref: a new refresh identity must not restart polling
  const refreshRef = useRef(refresh);
  useLayoutEffect(() => { refreshRef.current = refresh; }, [refresh]);
  useEffect(() => {
    if (!enabled) { setState("cancel"); return undefined; }
    if (!sessionId) { setState("failed"); return undefined; }
    setState("processing");
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") { await refreshRef.current(); if (alive) setState("success"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) {
        if ([401, 403, 404].includes(e?.response?.status)) { if (alive) setState("failed"); return; }
      }
      if (++tries >= MAX_TRIES) { if (alive) setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [sessionId, enabled]);
  return state;
}

export default function PaymentResult() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const state = usePaymentStatus(sessionId, !pathname.endsWith("/cancel"), refresh);
  const [Icon, cls, key] = VIEWS[state];
  const success = state === "success";
  return (
    <div className="max-w-md mx-auto py-16 text-center space-y-6 fade-up" data-testid={`payment-${state}`}>
      <Icon className={`w-16 h-16 mx-auto ${cls}`} />
      <h1 className="text-2xl font-extrabold">{t(key)}</h1>
      {state !== "processing" && (
        <Link to={success ? "/" : "/plans"} data-testid="payment-back-link" className="inline-flex rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white">
          {success ? t("nav.dashboard") : t("pay.back")}
        </Link>
      )}
    </div>
  );
}
